import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { listDecks, listTemplates, createDeck, deleteDeck, duplicateDeck } from "@/lib/api";
import { Plus, Copy, Trash2, ExternalLink, LogOut, Share2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", template: "blank" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([listDecks(), listTemplates()]);
      setDecks(d); setTemplates(t);
    } catch (e) { toast.error("Failed to load decks."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Please add a title."); return; }
    setCreating(true);
    try {
      const d = await createDeck(form);
      toast.success("Deck created");
      setOpen(false); setForm({ title: "", description: "", template: "blank" });
      navigate(`/admin/decks/${d.id}`);
    } catch (e) { toast.error("Failed to create deck."); }
    finally { setCreating(false); }
  };

  const onDuplicate = async (id) => {
    try { await duplicateDeck(id); await load(); toast.success("Deck duplicated"); }
    catch { toast.error("Duplicate failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this deck? This cannot be undone.")) return;
    try { await deleteDeck(id); setDecks((ds) => ds.filter((x) => x.id !== id)); toast.success("Deck deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const doLogout = async () => { await signOut(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[var(--layor-ivory)]" data-testid="page-admin">
      <div className="border-b border-[var(--layor-border)] bg-white">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <span className="font-display-black text-lg text-[var(--layor-navy)]">LAYOR</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[var(--layor-muted)] border-l border-[var(--layor-border)] pl-3">Slide Deck Builder</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/admin/testimonials" className="text-sm text-[var(--layor-muted)] hover:text-[var(--layor-navy)] hidden sm:inline">Testimonials</Link>
            <Link to="/" className="text-sm text-[var(--layor-muted)] hover:text-[var(--layor-navy)]">View site</Link>
            <span className="text-sm text-[var(--layor-navy)] hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Your Decks</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[var(--layor-navy)]">Design, share, and refine every deck in one place.</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold" data-testid="new-deck">
                <Plus size={16} /> New deck
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create a new deck</DialogTitle></DialogHeader>
              <form onSubmit={onCreate} className="space-y-4">
                <div>
                  <Label>Deck title *</Label>
                  <Input required data-testid="new-deck-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Investor Pitch, Acme Inc." />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label>Template</Label>
                  <Select value={form.template} onValueChange={(v) => setForm({ ...form, template: v })}>
                    <SelectTrigger data-testid="new-deck-template"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.name}, {t.slide_count} slides</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <button type="submit" disabled={creating} className="btn-primary inline-flex rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="new-deck-submit">
                    {creating ? "Creating…" : "Create deck"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="text-sm text-[var(--layor-muted)]">Loading…</div>
          ) : decks.length === 0 ? (
            <div className="border border-dashed border-[var(--layor-border)] rounded-2xl p-10 text-center bg-white">
              <div className="font-display text-xl text-[var(--layor-navy)]">No decks yet.</div>
              <p className="mt-2 text-sm text-[var(--layor-muted)]">Create your first deck to get started. Pick a template or start blank.</p>
              <button className="btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold" onClick={() => setOpen(true)}>
                <Plus size={16} /> New deck
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {decks.map((d) => (
                <div key={d.id} className="lift-card bg-white border border-[var(--layor-border)] rounded-2xl overflow-hidden" data-testid={`deck-card-${d.id}`}>
                  <div className="h-32 bg-gradient-to-br from-[var(--layor-navy)] to-[var(--layor-blue)] text-white p-5 flex flex-col justify-between">
                    <div className="eyebrow text-white/70">{d.slides?.length || 0} slides</div>
                    <div className="font-display text-lg leading-snug line-clamp-2">{d.title}</div>
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-[var(--layor-muted)]">Updated {new Date(d.updated_at).toLocaleString()}</div>
                    <div className="mt-1 text-xs">
                      {d.is_public ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700"><Share2 size={12} /> Public link on</span>
                      ) : (
                        <span className="text-[var(--layor-muted)]">Private</span>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link to={`/admin/decks/${d.id}`} className="btn-primary rounded-full px-4 py-2 text-xs font-semibold" data-testid={`edit-${d.id}`}>Edit</Link>
                      {d.is_public && <Link to={`/d/${d.slug}`} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold"><ExternalLink size={12} /> View</Link>}
                      <button onClick={() => onDuplicate(d.id)} className="btn-ghost inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold"><Copy size={12} /> Duplicate</button>
                      <button onClick={() => onDelete(d.id)} className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={12} /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
